import { useCallback, useState, useEffect, useRef, UIEvent } from 'react';
import { Flex } from 'components/box';
import { ProductGalleryItemInterface } from 'types/types';
import { isGalleryVideo } from 'types/guards/product';
import SoldOutMask from 'components/sold-out-mask';
import ThumbnailBullets from 'components/_shared/widgets/thumbnail-bullets';
import resourceUrl from '../../../../../util/make-url';
import { css } from '@emotion/react';
import dynamic from 'next/dynamic';
import ProductLabel from './product-label';

const EmbeddedVideo = dynamic(
  () => import('components/_shared/widgets/embedded-video')
);

const HIDE_UI_TIMEOUT = 3500;
const FADE_IN_TIME = 0.2;

const Swiper = ({
  media,
  activeMediaIndex,
  setActiveMediaIndex,
  soldOut,
  handleBulletVisibility,
  isPromotionalDeal,
  price,
}: {
  media: ProductGalleryItemInterface[];
  activeMediaIndex: number;
  setActiveMediaIndex: (num: number) => void;
  soldOut: boolean;
  handleBulletVisibility: () => void;
  isPromotionalDeal?: boolean;
  price?: string;
}) => {
  const portal = useRef<HTMLDivElement>(null);

  const [slideWidth, setSlideWidth] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [soldOutMaskVisible, setSoldOutMaskVisible] = useState(soldOut);

  const hideSoldOutMask = useCallback(
    () => void setSoldOutMaskVisible(false),
    []
  );

  const showSoldOutMask = useCallback(() => {
    soldOut && setSoldOutMaskVisible(true);
  }, [soldOut]);

  useEffect(() => {
    const setSliderWindowSize = () => {
      portal.current && setSlideWidth(portal.current.clientWidth);
    };

    setSliderWindowSize();

    window.addEventListener('resize', setSliderWindowSize, {
      passive: true,
    });
    return () => {
      window.removeEventListener('resize', setSliderWindowSize);
    };
  }, []);

  useEffect(() => {
    const portalRef = portal.current;
    let scrollTimer: NodeJS.Timeout | undefined;

    const handleSliderScroll = e => {
      if (portalRef) {
        setIsScrolling(true);
        setActiveMediaIndex(
          Math.round(e?.currentTarget.scrollLeft / slideWidth)
        );
        handleBulletVisibility();
        scrollTimer && clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const event = new CustomEvent('scrollend');
          portalRef.dispatchEvent(event);
        }, 200); // Change this value as needed to adjust the sensitivity of the scrollend event
      }
    };

    const handleScrollEnd = () => {
      setIsScrolling(false);
    };

    portalRef?.addEventListener('scroll', handleSliderScroll, {
      passive: true,
    });
    portalRef?.addEventListener('scrollend', handleScrollEnd, {
      passive: true,
    });
    return () => {
      portalRef?.removeEventListener('scroll', handleSliderScroll);
      portalRef?.removeEventListener('scrollend', handleScrollEnd);
    };
  }, [handleBulletVisibility, setActiveMediaIndex, slideWidth]);

  /**
   * Jump to specific variant.
   */
  useEffect(() => {
    const portalRef = portal.current;
    if (!isScrolling && portalRef) {
      portalRef.scrollLeft = activeMediaIndex * slideWidth;
    }
  }, [activeMediaIndex, slideWidth, isScrolling]);

  return (
    <Flex
      ref={portal}
      css={theme => css`
        box-shadow: ${theme.shadows.products};
        padding-bottom: 100%;
        position: relative;
        overflow-x: scroll;
        overflow-y: hidden;
        scroll-snap-type: x mandatory;
        scrollbar-width: 0;
        scrollbar-color: transparent;
        -ms-overflow-style: none;
        margin-left: -16px;
        margin-right: -16px;
        margin-top: -16px;
        padding-top: 32px;
        ::-webkit-scrollbar {
          display: none;
        }
        ::-webkit-scrollbar-thumb {
          background: transparent;
        }
      `}
    >
      <Flex
        css={css`
          position: absolute;
          top: 0;
          backface-visibility: hidden;
          height: 100%;
          width: ${slideWidth * media.length}px;
        `}
      >
        {portal.current &&
          media.map((media, idx) => (
            <Flex
              css={css`
                position: relative;
                width: ${slideWidth}px;
                height: 100%;
                scroll-snap-stop: always;
                scroll-snap-align: center;
                align-self: left;
              `}
              p={0}
              key={media.file.url}
              onClick={hideSoldOutMask}
              onKeyDown={hideSoldOutMask}
              onBlur={showSoldOutMask}
              tabIndex={0}
              justifyContent="center"
              alignItems="center"
            >
              {soldOutMaskVisible && (
                <SoldOutMask
                  clock
                  text
                  isPromoDeal={isPromotionalDeal}
                  price={price}
                />
              )}
              {isGalleryVideo(media) ? (
                <EmbeddedVideo
                  title={media.label}
                  url={media.video.url}
                  provider={media.video.provider}
                  isVideoActive={activeMediaIndex === idx}
                />
              ) : (
                <img
                  alt={media.label || 'Product media'}
                  css={css`
                    max-width: 100%;
                    object-fit: contain;
                    width: ${slideWidth}px;
                    backface-visibility: hidden;
                    animation-duration: 1s;
                    animation-fill-mode: both;
                    animation-name: fadeIn;
                  `}
                  src={resourceUrl(media.file.url, {
                    width: 800,
                    height: 800,
                    bg: 'fff',
                    fit: 'fill',
                  })}
                />
              )}

              {media.label && <ProductLabel text={media.label} />}
            </Flex>
          ))}
      </Flex>
    </Flex>
  );
};

let timeoutId;

const MobileCarousel = ({
  media,
  activeMediaIndex,
  setActiveMediaIndex,
  isSoldOut,
  hideThumbnail = false,
  isPromotionalDeal,
  price,
  mobileBulletHeight,
}: {
  media: ProductGalleryItemInterface[];
  isSoldOut: boolean;
  activeMediaIndex: number;
  setActiveMediaIndex: (idx: number) => void;
  hideThumbnail?: boolean;
  isPromotionalDeal?: boolean;
  price?: string;
  mobileBulletHeight: string;
}) => {
  const maxPossibleIndex = media.length - 1;

  const activeThumbIndex = Math.min(activeMediaIndex, maxPossibleIndex);

  const [showBullets, setShowBullets] = useState(true);

  const handleBulletVisibility = useCallback(() => {
    setShowBullets(true);
    timeoutId && clearTimeout(timeoutId);
    timeoutId = setTimeout(() => setShowBullets(false), HIDE_UI_TIMEOUT);
  }, []);

  /**
   * Fade out after initial render.
   */
  useEffect(() => {
    timeoutId = setTimeout(() => setShowBullets(false), HIDE_UI_TIMEOUT);
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <>
      <Swiper
        media={media}
        setActiveMediaIndex={setActiveMediaIndex}
        activeMediaIndex={activeMediaIndex}
        soldOut={isSoldOut}
        handleBulletVisibility={handleBulletVisibility}
        isPromotionalDeal={isPromotionalDeal}
        price={price}
      />
      {showBullets && !hideThumbnail && (
        <ThumbnailBullets
          mobileBulletHeight={mobileBulletHeight}
          total={media.length}
          activeIndex={activeThumbIndex}
          css={css`
            animation: fadeIn ${FADE_IN_TIME}s;
            @keyframes fadeIn {
              0% {
                opacity: 0;
              }
              100% {
                opacity: 1;
              }
            }
          `}
        />
      )}
    </>
  );
};

export default MobileCarousel;
